import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'

import {
    getContractsByCustomerId,
    mergeContracts,
} from '@/modules/Customer/services'
import { yupResolver } from '@hookform/resolvers/yup'
import { schemaMergedContract } from './schema'
import { MergedContractPayload } from './types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface MergedContractModalProps extends ModalProps {
    contractIDs: number[]
    customerID: number
    handleClose: () => void
}

const MergedContractModal = ({
    onClose,
    open,
    contractIDs,
    customerID,
    handleClose,
}: MergedContractModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })
    const { data: contracts, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_CONTRACTS_LIST, contractIDs[0] || ''],
        queryFn: () =>
            getContractsByCustomerId({
                customer_id: customerID,
                page: 1,
                page_size: contractIDs?.length || 0,
                q: '',
                filters: JSON.stringify({
                    contract_id: {
                        $in: contractIDs,
                    },
                }),
            }),
        enabled: !!contractIDs?.length,
    })

    const { mutateAsync: mergeContractsMutate, isPending } =
        useMutationWithHandler(
            { mutationFn: mergeContracts },
            {
                successMessage: t('merge-contract-success'),
                reactQueryKeys: QUERY_KEYS.CONTRACTS,
                onSuccess: () => {
                    onClose()
                    refetch()
                    handleClose()
                },
            }
        )

    const formControl = useForm<MergedContractPayload>({
        defaultValues: {
            customer_id: customerID,
            contract_id: contractIDs[0] || 0,
            contract_reason: '',
        },
        resolver: yupResolver(schemaMergedContract as any),
    })

    const handleCreate = async (values: MergedContractPayload) => {
        mergeContractsMutate({
            ...values,
            merge_contract_ids: contractIDs?.filter(
                (id) => id !== values?.contract_id
            ),
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Dialog title={t('merge-contract-title')} onClose={onClose} open={open}>
            <GridHookForm
                className="pt-2"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'contract_id',
                            type: 'select',
                            label: t('contracts.merged.contract_id'),
                            placeholder: t('contracts.merged.contract_id'),
                            options: contracts?.results?.map((contract) => ({
                                label: contract?.contract_number_id,
                                value: contract?.contract_id,
                            })),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_value',
                            type: 'number',
                            label: t('contracts.merged.contract_value'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_reason',
                            type: 'textarea',
                            label: t('contracts.merged.contract_reason'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default MergedContractModal
